<template>
  <div class="status-manager">
    <BaseRoundButton
      v-if="hasUnfollowedTenders"
      @click.native="onFollow()"
      :tooltip-text="$t('actions.follow')"
      :tooltip-subtitle="$t('page.tenders.follow-tooltip-subtitle')"
      icon="bookmark"
      is-white
    />
    <BaseRoundButton
      v-else
      @click.native="onUnfollow()"
      :tooltip-text="$t('actions.unfollow')"
      :tooltip-subtitle="$t('page.tenders.follow-tooltip-subtitle')"
      icon="bookmark"
      is-white
      is-light
    />

    <BaseRoundButton
      v-if="hasUnsharedTenders"
      @click.native="onShare()"
      :tooltip-text="$t('actions.share')"
      :tooltip-subtitle="$t('page.tenders.share-tooltip-subtitle')"
      icon="share-2"
      is-white
    />
    <BaseRoundButton
      v-else
      @click.native="onUnshare()"
      :tooltip-text="$t('actions.unshare')"
      :tooltip-subtitle="$t('page.tenders.share-tooltip-subtitle')"
      icon="share-2"
      is-white
      is-light
    />

    <BaseRoundButton
      v-if="hasUnreadTenders"
      @click.native="onRead()"
      :tooltip-text="$t('actions.read')"
      icon="eye"
      is-white
    />

    <BaseRoundButton
      v-else
      @click.native="onUnread()"
      :tooltip-text="$t('actions.unread')"
      icon="eye"
      is-light
      is-white
    />

    <span class="status-manager__separator" />
    <BaseRoundButton
      icon="thumbs-down"
      is-white
      :tooltip-text="thumbDownTooltip.title"
      :tooltip-subtitle="thumbDownTooltip.subtitle"
      @click.native.stop="onBatchThumbsDown"
    />
    <BaseRoundButton
      icon="thumbs-up"
      is-white
      :tooltip-text="thumbUpTooltip.title"
      :tooltip-subtitle="thumbUpTooltip.subtitle"
      @click.native.stop="onBatchThumbsUp"
    />

    <span class="status-manager__separator" />

    <BaseRoundButton
      :disabled="tenantIsStandard()"
      @click.native="onExportTenders"
      icon="download"
      :isLoading="exportPending"
      is-white
      :tooltip-text="exportTooltip"
    />

    <BaseRoundButton
      v-if="isViewingArchived"
      @click.native="onUnarchive()"
      :tooltip-text="$t('actions.tender.unarchive')"
      icon="archive"
      is-white
    />
    <BaseRoundButton
      v-else
      @click.native="onArchive()"
      :tooltip-text="$t('actions.tender.archive')"
      icon="archive"
      is-red
      is-white
    />

    <span class="status-manager__separator" v-if="tenantHasOpportunitiesAccess()" />
    <BaseButton
      v-if="tenantHasOpportunitiesAccess()"
      @click.native="onCreateOpportunities"
      is-white
      is-square
      is-small
      has-icon-left
      :isLoading="opportunitiesPending"
      :disabled="allLinked || opportunitiesPending"
      class="tender-header__actions-item"
    >
      <BaseIcon :name="opportunitiesPending ? 'refresh-cw' : 'target'" />
      <!-- The reason is in the label, not a hover tooltip a disabled button may never show. -->
      {{
        allLinked
          ? $tc('actions.tender.already-converted', tenders.length)
          : $tc('actions.tender.convert-to-opportunity')
      }}
    </BaseButton>
    <TenderArchiveDialog
      ref="tenderArchiveDialog"
      @on-submit="onArchive"
      @on-ignore="onIgnoreArchive"
      :tendersLength="tenders.length"
    />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseButton from '@/components/base/BaseButton';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import TenderArchiveDialog from '@/components/page-tender-details/TenderArchiveDialog.vue';

import { tenantIsStandard } from '@/util/subscription-util';
import { tenantHasOpportunitiesAccess } from '@/util/module-access-util';
import { bus } from '@/main';
import { mapActions, mapGetters } from 'vuex';
import { Modes } from '@/models/modes';

export default {
  name: 'TendersActionsManager',
  components: {
    BaseIcon,
    BaseButton,
    BaseRoundButton,
    TenderArchiveDialog,
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
    tenders: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters('exportTenders', { exportPending: 'exportSelectedPending' }),
    ...mapGetters('opportunities', { opportunitiesPending: 'pending' }),
    ...mapGetters('searchFilters', ['searchFilters']),
    hasUnreadTenders() {
      return !this.tenders.every((t) => t.userTender && t.userTender.read === true);
    },
    hasUnfollowedTenders() {
      return !this.tenders.every((t) => t.userTender && t.userTender.followed === true);
    },
    hasUnsharedTenders() {
      return !this.tenders.every((t) => t.tenantTender && t.tenantTender.shared === true);
    },
    // Selected tenders that have no opportunity yet: the only ones a conversion creates.
    unlinkedTenderIds() {
      return this.tenders.filter((t) => !t.opportunities || t.opportunities.length === 0).map((t) => t.id);
    },
    allLinked() {
      return this.tenders.length > 0 && this.unlinkedTenderIds.length === 0;
    },
    // Archived is now a filter dimension: show "unarchive" when the archived filter is on.
    isViewingArchived() {
      return this.searchFilters.archived === true;
    },
    tenderIds() {
      return this.tenders.map((t) => t.id);
    },
    exportTooltip() {
      if (this.tenantIsStandard()) return this.$t('subscriptions.message');
      return this.$tc('page.tenders.export', this.tenders.length, { count: this.tenders.length });
    },
    thumbDownTooltip() {
      return {
        title: this.$tc('tender-details.thumbs-rating.down-message.title', this.tenders.length),
        subtitle: this.$t('tender-details.thumbs-rating.down-message.subtitle'),
      };
    },
    thumbUpTooltip() {
      return {
        title: this.$tc('tender-details.thumbs-rating.up-message.title', this.tenders.length),
        subtitle: this.$t('tender-details.thumbs-rating.up-message.subtitle'),
      };
    },
  },
  methods: {
    ...mapActions('exportTenders', ['getExportTenders']),
    ...mapActions('opportunities', ['addOpportunities']),
    ...mapActions('tenders', [
      'readMany',
      'unreadMany',
      'followMany',
      'unfollowMany',
      'archiveMany',
      'unarchiveMany',
      'shareMany',
      'unshareMany',
      'rateUpMany',
      'rateDownMany',
    ]),
    tenantIsStandard,
    tenantHasOpportunitiesAccess,
    onBatchThumbsDown() {
      const ids = this.tenders.map((t) => t.id);
      this.rateDownMany({ ids })
        .then(() => this.$toast.success(this.$tc('toast.rate-down', this.tenders.length)))
        .catch((error) => {
          this.$toast.error(error);
        });
      this.$refs['tenderArchiveDialog'].openDialog();
    },
    onIgnoreArchive() {
      this.$emit('tender-status-updated');
    },
    onBatchThumbsUp() {
      const ids = this.tenders.map((t) => t.id);
      this.rateUpMany({ ids })
        .then(() => {
          this.$toast.info(this.$tc('toast.rate-up', this.tenders.length));
          this.$emit('tender-status-updated');
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onRead() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.read', ids.length);

      this.readMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.unreadMany({ ids }));
          this.$emit('tender-status-updated');
        })
        .catch((error) => this.$toast.error(error));
    },
    onUnread() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.unread', ids.length);

      this.unreadMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.readMany({ ids }));
          this.$emit('tender-status-updated');
        })
        .catch((error) => this.$toast.error(error));
    },
    onFollow() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.follow', ids.length);

      this.followMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.unfollowMany({ ids }).then(() => bus.$emit('refresh-page-requested')));
          this.$emit('tender-status-updated');
        })
        .catch((error) => this.$toast.error(error));
    },
    onUnfollow() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.unfollow', ids.length);

      this.unfollowMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.followMany({ ids }).then(() => bus.$emit('refresh-page-requested')));
        })
        .catch((error) => this.$toast.error(error));
    },
    onShare() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.share', ids.length);

      this.shareMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.unshareMany({ ids }).then(() => bus.$emit('refresh-page-requested')));
          this.$emit('tender-status-updated');
        })
        .catch((error) => this.$toast.error(error));
    },
    onUnshare() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.unshare', ids.length);

      this.unshareMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.shareMany({ ids }).then(() => bus.$emit('refresh-page-requested')));
        })
        .catch((error) => this.$toast.error(error));
    },
    onArchive() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.archive', ids.length);

      this.archiveMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () =>
            this.unarchiveMany({ ids }).then(() => bus.$emit('refresh-page-requested')),
          );
        })
        .then(() => {
          bus.$emit('refresh-page-requested');
          this.$emit('tender-status-updated');
        })
        .catch((error) => this.$toast.error(error));
    },
    onUnarchive() {
      const ids = this.tenderIds;
      const message = this.$tc('toast.unarchive', ids.length);

      this.unarchiveMany({ ids })
        .then(() => {
          this.$toast.cancel(message, () => this.archiveMany({ ids }).then(() => bus.$emit('refresh-page-requested')));
        })
        .then(() => {
          bus.$emit('refresh-page-requested');
          this.$emit('tender-status-updated');
        })
        .catch((error) => this.$toast.error(error));
    },
    async onCreateOpportunities() {
      if (this.opportunitiesPending) return;

      // Convert what can be converted; tenders that already have an opportunity
      // are skipped and reported, rather than blocking the whole selection.
      const tenderIds = this.unlinkedTenderIds;
      const skipped = this.tenders.length - tenderIds.length;
      if (tenderIds.length === 0) return;

      try {
        await this.addOpportunities({ tenderIds });
        this.$toast.success(this.$tc('toast.convert-to-opportunity', tenderIds.length));
        if (skipped > 0) {
          this.$toast.show(this.$tc('toast.convert-to-opportunity-skipped', skipped, { count: skipped }));
        }
      } catch (error) {
        this.$toast.error(error);
      } finally {
        // Also after an error: some opportunities may have been saved, and the
        // list must show them as linked.
        this.$emit('tender-status-updated');
      }
    },
    onExportTenders() {
      const mode = Modes[`${this.mode}`];
      this.getExportTenders({ mode: mode, tenderIds: this.tenderIds }).catch((error) => {
        this.$toast.error(error);
      });
    },
  },
};
</script>

<style lang="scss">
.status-manager {
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  background-color: $color-white;
  border-radius: $border-radius-large;
  box-shadow: $shadow-dropdown, $shadow-default;
  font-size: $font-size-small;
  padding: 4px 12px 4px 54px;

  left: 10px;
  z-index: -1;

  @media screen and (min-width: 1200px) {
    left: 4px;
    top: 8px;
    z-index: -1;
  }

  &__separator {
    background: $color-border;
    display: block;
    height: 24px;
    margin: 0 8px;
    width: 1px;
  }
}
</style>
